/**
 * Fase da maternidade. O mobile mantinha uma cópia de
 * `@eva/schemas/maternity-phase.schema.ts` porque não importava o pacote em
 * runtime; aqui o import é real (o app vive no monorepo), e só o que era
 * exclusivo da tela fica: a derivação para a figura paterna e o rótulo.
 */
import {
  resolveMaternityPhase,
  type MaternityPhase,
  type MaternityPhaseResult,
  type PhaseInputPregnancy,
} from '@eva/schemas';

export type { MaternityPhase, MaternityPhaseResult, PhaseInputPregnancy };
export { resolveMaternityPhase };

export const MATERNITY_PHASE_ORDER: MaternityPhase[] = ['TENTANTE', 'GESTANTE', 'PUERPERA', 'MAE_DE_ANJO', 'ADOTANTE', 'MAE'];

export const MATERNITY_PHASE_LABELS: Record<MaternityPhase, string> = {
  TENTANTE: 'Tentante',
  GESTANTE: 'Gestante',
  PUERPERA: 'Puérpera',
  MAE_DE_ANJO: 'Perdi meu bebê',
  ADOTANTE: 'Adotante',
  MAE: 'Mãe',
};

/** Tipos de usuária cuja fase vem do cadastro e não evolui no tempo. */
export const NON_TEMPORAL_USER_TYPES = ['TENTANTE', 'MAE_DE_ANJO', 'ADOTANTE', 'MAE'] as const;

/**
 * A figura paterna não tem fase própria: acompanha a de quem convidou. O
 * `fatherFigureFor` diz qual perfil ela acompanha, e `POSTPARTUM` ali é o
 * nome antigo de PUERPERA.
 */
export function resolvePhaseForUser(
  userType: string | undefined | null,
  pregnancy: PhaseInputPregnancy | null | undefined,
  fatherFigureFor?: string | null,
  agora: Date = new Date()
): MaternityPhaseResult {
  if (userType !== 'FATHER_FIGURE') {
    return resolveMaternityPhase({ userType, pregnancy }, agora);
  }
  if (fatherFigureFor === 'POSTPARTUM') {
    return resolveMaternityPhase({ userType: 'PREGNANT', pregnancy }, agora);
  }
  if (fatherFigureFor && fatherFigureFor !== 'PREGNANT') {
    return resolveMaternityPhase({ userType: fatherFigureFor }, agora);
  }
  return resolveMaternityPhase({ userType: 'PREGNANT', pregnancy }, agora);
}

/** Rótulo em primeira pessoa usado no cabeçalho da home. */
export function phaseLabel(phase: MaternityPhase, isFatherFigure: boolean): string {
  if (isFatherFigure) return `Acompanhante – ${MATERNITY_PHASE_LABELS[phase]}`;
  const eu: Record<MaternityPhase, string> = {
    TENTANTE: 'Eu sou tentante',
    GESTANTE: 'Eu sou gestante',
    PUERPERA: 'Eu sou puérpera',
    MAE_DE_ANJO: 'Perdi meu bebê',
    ADOTANTE: 'Eu sou adotante',
    MAE: 'Eu sou mãe',
  };
  return eu[phase];
}
